import TracesListModal from '@components/traces/traces-list-modal';
import useAppContext from '@hooks/useAppContext';
import FOCityLedgerDrawer from '@modules/front-office/components/city-ledger/fo-cityledger-drawer-container';
import FOCashBreakdownDrawer from '@modules/front-office/components/fo-cash-breakdown.drawer';
import { PDFReservationForm } from '@modules/front-office/components/reservation/voucher/fo-reservation-registration-form-voucher';
import POSOthersDrawer from '@modules/point-of-sale/components/pos-landing-page/pos-order-taking-window/table-drawer/pos-others.drawer';
import {
  selectPOSHasPrinter,
  selectPOSShiftRole,
} from '@modules/point-of-sale/slice/pos-selector';
import { Clear, CloseOutlined, SearchOutlined } from '@mui/icons-material';
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  onOpen as onOpenSlice,
  onClose as onCloseSlice,
} from '@store/slices/disclosure.slice';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ServoLogo from '../../assets/servo-logo.svg';
import ServoTextLogo from '../../assets/servo-text-logo.svg';
import { getColor } from '../../assets/theme';
import Image from '../../components/ui/image';
import NavBtn from '../../components/ui/navbtn';
import Version from '../../components/ui/version';
import useLayoutContext from '../../hooks/useLayoutContext';
import { Module, Navigation } from '../../interfaces/module.interface';
import FORoomPostingContainer from '../../modules/front-office/containers/fo-room_posting.container';
import { useAppDispatch, useAppSelector } from '../../store';
import { moduleArr } from '../../utils/module-arr';
import { useCurrentModule } from '@hooks/useCurrentModule';

type TSidebarDisclosure =
  | 'room-posting'
  | 'city-ledger'
  | 'blank-reservation-form'
  | 'charging-station'
  | 'traces'
  | 'cash-breakdown'
  | 'pos-misc'
  | 'pos-reprint'
  | 'pos-void-invoice'
  | 'shift-reports'
  | 'yz-reading'
  | 'force-end-shift';

import { useLazyGetPOSDeclareNewDay } from '@api/pos/pos-work-period.api.rtk';
import POSReprintVoidModal from '@modules/point-of-sale/components/pos-landing-page/pos-order-taking-window/others/pos-reprint-void.modal';
import POSShiftReportModal from '@modules/point-of-sale/components/shift-reports/pos-shift-reports-modal';
import POSYZReadingModal from '@modules/point-of-sale/components/yz-reading/pos-yz-reading-modal';
import { selectPOSstationID } from '@modules/point-of-sale/slice/pos-selector';
import { usePOSMediumScreen } from '@utils/screen-size';
import { selectSalesDateByPath } from '@store/selector/app.selector';
import ForceEndShiftModal from '@modules/point-of-sale/components/shift-reports/pos-force-end-shift-modal';

const Sidebar = () => {
  // #region STATE
  const dispatch = useAppDispatch();
  const isPOSMediumScreen = usePOSMediumScreen();
  const { isSidebarOpen, toggleSidebar } = useLayoutContext();
  const {
    genpar: { getGenparValue },
    openSecCodeDialog,
    startLoadingStatus,
    stopLoadingStatus,
    toast,
  } = useAppContext();

  const { bgColor, white, danger } = getColor();

  const [value, setValue] = useState<string | null>('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState<Module>();
  const [forceCloseAccordion, setForceCloseAccordion] = useState(false);

  const smallScreen = useMediaQuery('(max-width: 425px)') || isPOSMediumScreen;
  const searchRef = useRef<HTMLInputElement | null>(null);
  const currentModuleState = useCurrentModule();
  const shiftRole = useAppSelector(selectPOSShiftRole);
  const stationID = useAppSelector(selectPOSstationID);
  const hasPrinter = useAppSelector(selectPOSHasPrinter);

  const isWaiter = shiftRole === 'Waiter';
  const pathname = window.location.pathname?.split('/')?.[1];

  const isBookingEngine = getGenparValue('be_enabled') === 'true';
  const isChannelManager = getGenparValue('cm_active') === 'true';

  const currentSalesDate = useAppSelector((state) =>
    selectSalesDateByPath(state, pathname),
  );

  const validateNav = useCallback(
    (nav: { title: string }) => {
      if (nav?.title === 'Booking Engine' && !isBookingEngine) return false;
      if (nav?.title === 'Channel Manager' && !isChannelManager) return false;
      return true;
    },
    [isBookingEngine, isChannelManager],
  );

  useEffect(() => {
    const current = moduleArr?.find(
      (item) => item.link.split('/')[1] === pathname,
    );

    if (!!!current) return;

    setCurrentModule(current);
  }, [currentModuleState, pathname]);

  useEffect(() => {
    if (isSidebarOpen) return;
    setValue('');
  }, [isSidebarOpen]);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  useEffect(() => {
    dispatch(onCloseSlice('dnd'));
  }, []);
  // #endregion

  // #region DISCLOSURE
  const [disclosure, setDisclosure] = useState<TSidebarDisclosure>();
  const onClose = () => setDisclosure(undefined);
  const onOpen = (title: TSidebarDisclosure) => setDisclosure(title);
  const onToggle = () => setDisclosure(undefined);
  const [transactionType, setTransactionType] = useState<
    'end-shift' | 'force-end-shift' | 'declare-new-day'
  >();

  const [checkDND] = useLazyGetPOSDeclareNewDay();

  const handleSidebarClick = async (title: string) => {
    if (pathname === 'front-office') {
      actions[title]?.();
      return;
    }

    if (pathname === 'point-of-sale') {
      if (title === 'Declare New Day') {
        try {
          startLoadingStatus('Checking Shift');
          await checkDND(currentSalesDate).unwrap();

          openSecCodeDialog(async (res) => {
            if (!res?.data.isValid) return;
            actions['Cashier Breakdown']?.();
            setTransactionType('declare-new-day');
          }, 'dec_new_day');
          return;
        } catch (error) {
          toast({ severity: 'warning', message: error });
        } finally {
          stopLoadingStatus();
        }
      }

      if (title === 'End Shift') {
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          actions['Cashier Breakdown']?.();
          setTransactionType('end-shift');
        }, 'force_end_shift');
        return;
      }

      if (title === 'Force End Shift') {
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          actions['Force End Shift']?.();
          setTransactionType('force-end-shift');
        }, 'force_end_shift');
        return;
      }

      actions[title]?.();
      return;
    }
  };

  const handleChildSidebarClick = (childTitle: string) => {
    handleSidebarClick(childTitle);

    if (pathname === 'point-of-sale') {
      if (childTitle === 'Shift Reports') {
        // openSecCodeDialog(async (res) => {
        // if (!res?.data.isValid) return;
        actions['Shift Reports']?.();
        // }, 'print_report');
        return;
      }
      if (childTitle === 'Y/Z Reading') {
        // openSecCodeDialog(async (res) => {
        // if (!res?.data.isValid) return;
        actions['Y/Z Reading']?.();
        // }, 'print_report');
        return;
      }

      actions[childTitle]?.();
      return;
    }
  };

  const actions: Record<string, () => void> = {
    'Room Posting': () => setDisclosure('room-posting'),
    'City Ledger': () => setDisclosure('city-ledger'),
    'Print Blank Reg Form': () => setDisclosure('blank-reservation-form'),
    Traces: () => setDisclosure('traces'),
    'Declare New Day': () => dispatch(onOpenSlice('dnd')),
    'Cashier Breakdown': () => setDisclosure('cash-breakdown'),
    Reprint: () => setDisclosure('pos-reprint'),
    'Void Invoice': () => setDisclosure('pos-void-invoice'),
    'Shift Reports': () => setDisclosure('shift-reports'),
    'Y/Z Reading': () => setDisclosure('yz-reading'),
    'Force End Shift': () => setDisclosure('force-end-shift'),
    Misc: () => setDisclosure('pos-misc'),
  };

  const renderComponent = () => {
    switch (disclosure) {
      case 'room-posting':
        return (
          <FORoomPostingContainer
            transactionDetails={{ folioUuid: '', folioStatus: 'in-house' }}
            isOpenModal
            onCloseModal={onClose}
          />
        );

      case 'city-ledger':
        return <FOCityLedgerDrawer isOpen onCloseDrawer={onClose} />;

      case 'blank-reservation-form':
        return (
          <PDFReservationForm
            {...{
              bookingReferenceId: '',
              isOpen: disclosure === 'blank-reservation-form',
              handleModalClose: () => {
                setDisclosure(undefined);
              },
              isBlank: true,
            }}
          />
        );

      case 'traces':
        return <TracesListModal isOpen onClose={onClose} />;

      case 'cash-breakdown':
        return (
          <FOCashBreakdownDrawer
            drawerDisclosure={{
              onOpen: () => onOpen('cash-breakdown'),
              onClose: () => {
                onClose();
                setTransactionType(undefined);
              },
              isOpen: true,
              onToggle,
            }}
            transactionType={transactionType}
          />
        );

      case 'pos-misc':
        return (
          <POSOthersDrawer
            modalDisclosure={{
              onOpen: () => onOpen('pos-misc'),
              onClose,
              isOpen: true,
              onToggle,
            }}
            isMisc={true}
          />
        );

      case 'pos-reprint':
        return (
          <POSReprintVoidModal
            modalDisclosure={{
              onOpen: () => onOpen('pos-reprint'),
              onClose,
              isOpen: true,
              onToggle,
            }}
            isAll={true}
          />
        );

      case 'pos-void-invoice':
        return (
          <POSReprintVoidModal
            modalDisclosure={{
              onOpen: () => onOpen('pos-void-invoice'),
              onClose,
              isOpen: true,
              onToggle,
            }}
            isVoid={true}
            isAll={true}
          />
        );

      case 'shift-reports':
        return (
          <POSShiftReportModal
            modalDisclosure={{
              onOpen: () => onOpen('shift-reports'),
              onClose,
              isOpen: true,
              onToggle,
            }}
          />
        );

      case 'yz-reading':
        return (
          <POSYZReadingModal
            referenceId={stationID}
            modalDisclosure={{
              onOpen: () => onOpen('yz-reading'),
              onClose,
              isOpen: true,
              onToggle,
            }}
          />
        );

      case 'force-end-shift':
        return (
          <ForceEndShiftModal
            modalDisclosure={{
              onOpen: () => onOpen('force-end-shift'),
              onClose,
              isOpen: true,
              onToggle,
            }}
          />
        );

      default:
        return null;
    }
  };
  // #endregion

  // #region Show Room Posting

  // const { BlankReservationFormModal } = useBlankRegistrationForm({
  //   bookingReferenceId: '',
  //   isOpen: disclosure === 'blank-reservation-form',
  // });
  // #endregion

  return (
    <>
      <Box
        id='sidebar'
        component='aside'
        sx={{
          width:
            isSidebarOpen && !smallScreen
              ? '300px'
              : !isSidebarOpen && !smallScreen
                ? '70px'
                : isSidebarOpen && smallScreen
                  ? '100%'
                  : '',
          minWidth:
            isSidebarOpen && !smallScreen
              ? '270px'
              : !isSidebarOpen && !smallScreen
                ? '70px'
                : isSidebarOpen && smallScreen
                  ? '100%'
                  : '',

          borderRight: `1.5px solid ${bgColor}`,
          transition: !smallScreen ? 'all ease .3s' : 'all ease .5s',
          position: smallScreen ? 'absolute' : 'sticky',
          left: isSidebarOpen && smallScreen ? '0' : '-110%',
          bgcolor: white,
          zIndex: 100,
          height: '100%',
        }}
      >
        {/* Logo Image */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '23px 17px',
            gap: '12px',
            justifyContent: 'space-between',
            height: '78px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Image img={ServoLogo} width={40} height={32} />
            {isSidebarOpen && (
              <Image img={ServoTextLogo} width={131} height={21} />
            )}
          </Box>

          {smallScreen && (
            <IconButton
              disableRipple
              sx={{
                border: 'unset',
                ':hover': { bgcolor: 'unset', color: danger },
              }}
              onClick={toggleSidebar}
            >
              <CloseOutlined />
            </IconButton>
          )}
        </Box>

        {/* Navigation */}
        <Box
          sx={{
            height: 'calc(100% - 78px)',
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
              width: !isSidebarOpen ? '6px' : 'default',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!isSidebarOpen && !searchOpen && (
              <Tooltip title='Search' placement='right'>
                <ListItemButton
                  sx={{
                    ':hover': { bgcolor: 'transparent' },
                    ':hover .nav__icon': { bgcolor: '#F5F5F5' },
                    height: '46px',
                    mb: 1,
                    ml: '2px',
                    mr: '2px',
                  }}
                  disableRipple
                  onClick={() => {
                    setSearchOpen(true);
                    toggleSidebar();
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '20px',
                      padding: '6px',
                      borderRadius: '6px',
                      transition: 'background .3s ease',
                    }}
                    className='nav__icon'
                  >
                    <SearchOutlined />
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            )}

            {isSidebarOpen && searchOpen && (
              <Box
                sx={{
                  overflow: 'hidden',
                  height: '46px',
                  width: '100%',
                  mb: 1,
                  ml: 2,
                  mr: 1,
                }}
              >
                <TextField
                  inputRef={searchRef}
                  label='Search'
                  size='small'
                  fullWidth
                  variant='standard'
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  onBlur={() => !value && setSearchOpen(false)}
                  InputProps={{
                    startAdornment: <SearchOutlined />,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          onClick={() => setValue('')}
                          sx={{
                            opacity: value ? 1 : 0,
                            pointerEvents: value ? 'auto' : 'none',
                            transition: 'opacity 0.2s',
                            bgcolor: 'transparent',
                          }}
                        >
                          <Clear fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '.MuiAutocomplete-popupIndicator': { display: 'none' },
                    input: { textIndent: 10 },
                  }}
                />
              </Box>
            )}
          </Box>
          {currentModule && (
            <List
              component='nav'
              className='nav'
              sx={{
                zIndex: 2,
                bgcolor: white,
                p: '0 0 2px 0',
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                height: !isSidebarOpen ? 'auto' : '100%',
                gap: 1,
              }}
            >
              {isSidebarOpen && !searchOpen && (
                <ListItemButton
                  sx={{
                    pl: 3,
                    gap: '12px',
                    height: '46px',
                    maxHeight: '46px',
                    minHeight: '46px',
                    ':hover': { bgcolor: 'transparent' },
                  }}
                  onClick={() => {
                    setSearchOpen(true);
                  }}
                  disableRipple
                >
                  <ListItemIcon
                    sx={{
                      minWidth: '20px',
                      borderRadius: '4px',
                      transition: 'background .3s ease',
                    }}
                    className='nav__icon'
                  >
                    <SearchOutlined />
                  </ListItemIcon>
                  <ListItemText
                    primary='Search'
                    sx={{
                      fontSize: '14px!important',
                      margin: 'unset',
                      marginBottom: '-3.5px',
                    }}
                    className='nav__title'
                  />
                </ListItemButton>
              )}

              {currentModule?.sidebar
                ?.filter(
                  (item: {
                    title: string;
                    accordion?: { title: string }[];
                  }) => {
                    // Check if the main title matches the search term
                    if (
                      item.title
                        .toLowerCase()
                        .includes(value?.toLowerCase() || '')
                    ) {
                      return item;
                    }

                    // Check if any accordion title matches the search term
                    if (
                      item.accordion?.some((subItem) =>
                        subItem.title
                          .toLowerCase()
                          .includes(value?.toLowerCase() || ''),
                      )
                    ) {
                      return item;
                    }

                    return false;
                  },
                )
                ?.map((item: Navigation, i: number) => {
                  const filteredAccordions =
                    item.accordion?.filter(validateNav) || [];

                  /*
                    Disable button if: 
                    pathname = 'point-of-sale', shiftRole = 'Waiter'
                    item.title = 'End Shift' or 'Declare New Day'
                  */
                  const isDisabledForWaiter =
                    isWaiter &&
                    pathname === 'point-of-sale' &&
                    (item.title === 'End Shift' ||
                      item.title === 'Force End Shift' ||
                      item.title === 'Declare New Day' ||
                      item.title === 'Void Invoice');

                  const noPrinter = !hasPrinter && item.title === 'Reprint';

                  return (
                    <Tooltip
                      title={noPrinter ? 'No printer is configured.' : ''}
                      placement='right'
                    >
                      <span>
                        <NavBtn
                          {...{
                            ...item,
                            ...(filteredAccordions.length && {
                              accordion: filteredAccordions,
                            }),
                            moduleCode: currentModule.code,
                            isOpen: isSidebarOpen,
                            toggleSidebar,
                            setForceCloseAccordion,
                            forceCloseAccordion,
                            disabled: isDisabledForWaiter || noPrinter,
                          }}
                          key={i}
                          onClick={() => handleSidebarClick(item.title)}
                          onChildClick={handleChildSidebarClick}
                        />
                      </span>
                    </Tooltip>
                  );
                })}

              <Version isOpen={isSidebarOpen} />
            </List>
          )}
        </Box>
      </Box>

      <>{renderComponent()}</>
    </>
  );
};

export default memo(Sidebar);
